import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface ConversationPreview {
  id: string;
  otherUser: {
    id: string;
    first_name: string | null;
    avatar_url: string | null;
  };
  lastMessage: string | null;
  lastMessageAt: string | null;
  lastMessageSenderId: string | null;
  unreadCount: number;
  updatedAt: string;
  isFriend: boolean;
  isNoticeboard: boolean;
}

export function useConversations({ enabled = true }: { enabled?: boolean } = {}) {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<ConversationPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalUnread, setTotalUnread] = useState(0);

  const fetchConversations = useCallback(async () => {
    if (!user || !enabled) {
      setConversations([]);
      setTotalUnread(0);
      setLoading(false);
      return;
    }

    try {
      // Get all conversation IDs the user participates in
      const { data: participations, error: pError } = await supabase
        .from("conversation_participants")
        .select("conversation_id, last_read_at")
        .eq("user_id", user.id);

      if (pError) throw pError;
      if (!participations || participations.length === 0) {
        setConversations([]);
        setTotalUnread(0);
        setLoading(false);
        return;
      }

      const convIds = participations.map((p) => p.conversation_id);
      const lastReadMap = new Map(participations.map((p) => [p.conversation_id, p.last_read_at]));

      // Identify conversations that originated from a noticeboard listing
      const { data: convRows } = await supabase
        .from("conversations")
        .select("id, noticeboard_post_id")
        .in("id", convIds);
      const noticeboardConvSet = new Set<string>(
        (convRows || []).filter((c: any) => c.noticeboard_post_id).map((c: any) => c.id)
      );

      // Get other participants for each conversation
      const { data: allParticipants, error: apError } = await supabase
        .from("conversation_participants")
        .select("conversation_id, user_id")
        .in("conversation_id", convIds)
        .neq("user_id", user.id);

      if (apError) throw apError;

      const otherUserMap = new Map<string, string>();
      (allParticipants || []).forEach((p) => {
        otherUserMap.set(p.conversation_id, p.user_id);
      });

      // Fetch profiles for other users. Use the conversation-partner RPC so
      // we still see name/avatar of users who have hidden their profile from
      // the directory — they remain hidden everywhere else.
      const otherUserIds = [...new Set(otherUserMap.values())];
      const { data: profiles } = await supabase.rpc(
        "get_conversation_partner_profiles" as any,
        { user_ids: otherUserIds } as any
      );

      const profileMap = new Map(((profiles as any[]) || []).map((p: any) => [p.id, p]));

      // Determine which other users are friends with current user
      const { data: friendRows } = await supabase
        .from("friend_requests")
        .select("sender_id, receiver_id")
        .eq("status", "accepted")
        .or(`and(sender_id.eq.${user.id},receiver_id.in.(${otherUserIds.join(",")})),and(receiver_id.eq.${user.id},sender_id.in.(${otherUserIds.join(",")}))`);
      const friendSet = new Set<string>();
      (friendRows || []).forEach((r: any) => {
        friendSet.add(r.sender_id === user.id ? r.receiver_id : r.sender_id);
      });

      const earliestLastRead = participations.reduce<string | null>((earliest, participation) => {
        const value = participation.last_read_at || new Date(0).toISOString();
        if (!earliest || new Date(value).getTime() < new Date(earliest).getTime()) return value;
        return earliest;
      }, null);

      const [lastMessagesResult, unreadMessagesResult] = await Promise.all([
        supabase
          .from("direct_messages")
          .select("conversation_id, message, created_at, sender_id, deleted_at, hidden_for")
          .in("conversation_id", convIds)
          .order("created_at", { ascending: false })
          .limit(Math.min(Math.max(convIds.length * 10, 50), 1000)),
        supabase
          .from("direct_messages")
          .select("conversation_id, created_at")
          .in("conversation_id", convIds)
          .neq("sender_id", user.id)
          .gt("created_at", earliestLastRead || new Date(0).toISOString()),
      ]);

      if (lastMessagesResult.error) throw lastMessagesResult.error;
      if (unreadMessagesResult.error) throw unreadMessagesResult.error;

      const lastMessageMap = new Map<string, { message: string | null; created_at: string; sender_id: string }>();
      (lastMessagesResult.data || []).forEach((msg: any) => {
        // Skip messages this user has hidden ("Delete for me").
        if (Array.isArray(msg.hidden_for) && msg.hidden_for.includes(user.id)) return;
        if (!lastMessageMap.has(msg.conversation_id)) lastMessageMap.set(msg.conversation_id, msg);
      });

      const unreadCountMap = new Map<string, number>();
      (unreadMessagesResult.data || []).forEach((msg) => {
        const lastRead = lastReadMap.get(msg.conversation_id) || new Date(0).toISOString();
        if (new Date(msg.created_at).getTime() > new Date(lastRead).getTime()) {
          unreadCountMap.set(msg.conversation_id, (unreadCountMap.get(msg.conversation_id) || 0) + 1);
        }
      });

      // Build previews from batched last message + unread queries.
      const previews: ConversationPreview[] = [];
      let unreadTotal = 0;

      for (const convId of convIds) {
        const otherId = otherUserMap.get(convId);
        if (!otherId) continue;

        const profile = profileMap.get(otherId);
        const lastMsg = lastMessageMap.get(convId);
        const unread = unreadCountMap.get(convId) || 0;
        unreadTotal += unread;

        previews.push({
          id: convId,
          otherUser: {
            id: otherId,
            first_name: profile?.first_name || "Unknown",
            avatar_url: profile?.avatar_url || null,
          },
          lastMessage: lastMsg
            ? (lastMsg as any).deleted_at
              ? "This message was deleted"
              : lastMsg.message
            : null,
          lastMessageAt: lastMsg?.created_at || null,
          lastMessageSenderId: lastMsg?.sender_id || null,
          unreadCount: unread,
          updatedAt: lastMsg?.created_at || new Date(0).toISOString(),
          isFriend: friendSet.has(otherId),
          isNoticeboard: noticeboardConvSet.has(convId),
        });
      }

      // Sort by most recent message
      previews.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

      setConversations(previews);
      setTotalUnread(unreadTotal);
    } catch (err) {
      console.error("Error fetching conversations:", err);
    } finally {
      setLoading(false);
    }
  }, [user, enabled]);

  useEffect(() => {
    if (enabled) fetchConversations();
  }, [fetchConversations]);

  // Realtime: listen for new messages across all conversations
  useEffect(() => {
    if (!user || !enabled) return;

    const channel = supabase
      .channel(`dm-updates-${user.id}-${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "direct_messages" },
        () => {
          fetchConversations();
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "conversation_participants" },
        () => {
          fetchConversations();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, enabled, fetchConversations]);

  // Optimistically clear unread badge when a conversation is marked read
  // elsewhere in the app (realtime UPDATE on conversation_participants isn't
  // in the publication, so we listen for a local event instead).
  useEffect(() => {
    const handler = (e: Event) => {
      const convId = (e as CustomEvent).detail?.conversationId as string | undefined;
      if (!convId) return;
      setConversations((prev) => {
        let delta = 0;
        const next = prev.map((c) => {
          if (c.id === convId && c.unreadCount > 0) {
            delta += c.unreadCount;
            return { ...c, unreadCount: 0 };
          }
          return c;
        });
        if (delta > 0) setTotalUnread((t) => Math.max(0, t - delta));
        return next;
      });
    };
    window.addEventListener("conversation-read", handler);
    return () => window.removeEventListener("conversation-read", handler);
  }, []);

  const deleteConversation = useCallback(async (conversationId: string) => {
    if (!user) return;
    // Remove user's participation (effectively deletes the conversation for them)
    await supabase
      .from("conversation_participants")
      .delete()
      .eq("conversation_id", conversationId)
      .eq("user_id", user.id);
    
    // Optimistically remove from local state
    setConversations((prev) => prev.filter((c) => c.id !== conversationId));
    setTotalUnread((prev) => {
      const conv = conversations.find((c) => c.id === conversationId);
      return prev - (conv?.unreadCount || 0);
    });
  }, [user, conversations]);

  return { conversations, loading, totalUnread, refetch: fetchConversations, deleteConversation };
}
